import type { ReactNode } from 'react';
import { Link } from 'react-router';

type LinkMatch = {
  index: number;
  full: string;
  path: string;
  label: string;
  stripLead: RegExp;
};

/**
 * Matches deep links like:
 * `/iap/{id}?openTab=Actions&openAction={uuid}`
 * Optionally preceded by `Open the task «Name»: ` or `Open the task: `.
 */
const TASK_LINK_PATTERN =
  /(?:Open the task(?:\s*«([^»]+)»)?\s*:\s*)?(\/iap\/[^\s?]+?\?openTab=Actions&openAction=[0-9a-fA-F-]{36})/g;

/**
 * Matches deep links like:
 * `/iap/{id}?openTab=Resources&openRequest={uuid}`
 * Optionally preceded by `Open the 213RR «Name»: ` or `Open the 213RR: `.
 */
const RESOURCE_REQUEST_LINK_PATTERN =
  /(?:Open the 213RR(?:\s*«([^»]+)»)?\s*:\s*)?(\/iap\/[^\s?]+?\?openTab=Resources&openRequest=[0-9a-fA-F-]{36})/g;

export function buildTaskOpenAppMessage(parts: {
  prefix: string;
  taskName: string;
  path: string;
}): string {
  const name = parts.taskName.trim() || 'task';
  return `${parts.prefix.trim()} Open the task «${name}»: ${parts.path}`;
}

export function buildResourceRequestOpenAppMessage(parts: {
  prefix: string;
  itemName: string;
  path: string;
}): string {
  const name = parts.itemName.trim() || 'ICS-213RR';
  return `${parts.prefix.trim()} Open the 213RR «${name}»: ${parts.path}`;
}

function collectMatches(
  text: string,
  pattern: RegExp,
  fallbackLabel: string,
  namedLead: RegExp,
  bareLead: RegExp
): LinkMatch[] {
  const out: LinkMatch[] = [];
  const re = new RegExp(pattern.source, 'g');
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const [full, capturedName, path] = match;
    out.push({
      index: match.index,
      full,
      path,
      label: capturedName?.trim() || fallbackLabel,
      stripLead: capturedName ? namedLead : bareLead,
    });
  }
  return out;
}

/** Split appMessage into text and Links; hides raw /iap paths for tasks and 213RRs. */
export function renderAppMessageWithTaskLinks(
  message: string | null | undefined
): ReactNode {
  const text = message ?? '';
  if (!text.trim()) return 'Notification';

  const matches = [
    ...collectMatches(
      text,
      TASK_LINK_PATTERN,
      'Open task',
      /Open the task\s*«[^»]*»\s*:?\s*$/i,
      /Open the task\s*:?\s*$/i
    ),
    ...collectMatches(
      text,
      RESOURCE_REQUEST_LINK_PATTERN,
      'ICS-213RR',
      /Open the 213RR\s*«[^»]*»\s*:?\s*$/i,
      /Open the 213RR\s*:?\s*$/i
    ),
  ].sort((a, b) => a.index - b.index);

  if (matches.length === 0) return text;

  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  for (const entry of matches) {
    if (entry.index < lastIndex) continue;
    if (entry.index > lastIndex) {
      nodes.push(text.slice(lastIndex, entry.index));
    }
    const beforeLink = entry.full.replace(entry.path, '').trim();
    if (beforeLink) {
      const lead = beforeLink.replace(entry.stripLead, '').trim();
      if (lead) nodes.push(`${lead} `);
    }
    nodes.push(
      <Link
        key={`${entry.path}-${entry.index}`}
        to={entry.path}
        className="tw:font-medium tw:text-primary tw:underline tw:underline-offset-2"
        onClick={(event) => event.stopPropagation()}
      >
        {entry.label}
      </Link>
    );
    lastIndex = entry.index + entry.full.length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  if (nodes.length === 0) return text;
  return <>{nodes}</>;
}
